import { useEffect, useState } from "react";
import axios from "axios";
import { Globe, RefreshCw, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Wrench, X } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

/**
 * Country Code Audit widget (Fabrizio bug 06/07/2026)
 *
 * Mostra in tempo reale quanti record ``dealers`` e ``customers`` hanno
 * il ``country`` in forma ISO (drift GB/IE/GI → dovrebbero essere
 * UK/IRL/GBZ per allineamento con backend PDF/wallet) o null/vuoto.
 *
 * Read-only: nessun impatto sul DB. Utile per validare l'entità del
 * drift prima di decidere se lanciare un allineamento manuale.
 */

const STATUS_META = {
  ok: {
    label: "OK",
    Icon: CheckCircle2,
    tone: "emerald",
    text: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/40",
  },
  warn: {
    label: "WARN",
    Icon: AlertCircle,
    tone: "amber",
    text: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/40",
  },
  needs_review: {
    label: "DA RIVEDERE",
    Icon: AlertCircle,
    tone: "red",
    text: "text-red-300",
    bg: "bg-red-500/10",
    border: "border-red-500/50",
  },
};

const ROW_ICON = {
  ok: "✅",
  iso_drift: "🔴",
  null: "⚠️",
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return iso;
  }
};

export const CountryAuditWidget = () => {
  const { getAuthHeader, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Solo super_admin (l'endpoint è protetto server-side, ma nascondiamo
  // anche in UI per non generare click frustranti).
  const canView = user?.role === "super_admin";

  const fetchAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/admin/system/country-audit`,
        getAuthHeader(),
      );
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const applyLegacy = async () => {
    setApplying(true);
    setApplyResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/system/country-audit/apply-legacy`,
        {},
        getAuthHeader(),
      );
      setApplyResult(r.data);
      // Auto-refresh dell'audit per riflettere lo stato post-fix
      await fetchAudit();
    } catch (e) {
      setApplyResult({ error: e.response?.data?.detail || e.message || "Errore" });
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    if (canView) fetchAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  if (!canView) return null;

  const status = data?.summary?.status || "ok";
  const meta = STATUS_META[status] || STATUS_META.ok;
  const StatusIcon = meta.Icon;

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="country-audit-widget"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${meta.bg} border ${meta.border} flex items-center justify-center`}>
            <Globe className={`w-5 h-5 ${meta.text}`} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Country Code Audit</h3>
            <p className="text-gray-500 text-xs">
              Verifica drift ISO↔legacy (GB↔UK · IE↔IRL · GI↔GBZ) su dealers e customers
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${meta.bg} ${meta.text} ${meta.border}`}
              data-testid="country-audit-status-badge"
            >
              <StatusIcon className="w-3.5 h-3.5" />
              {meta.label}
            </span>
          )}
          <button
            type="button"
            onClick={fetchAudit}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
            data-testid="country-audit-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
          {data?.summary?.total_drift > 0 && (
            <button
              type="button"
              onClick={() => { setShowApplyModal(true); setApplyResult(null); }}
              className="inline-flex items-center gap-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 text-red-200 px-3 py-1.5 rounded-lg text-xs font-semibold"
              data-testid="country-audit-fix-btn"
            >
              <Wrench className="w-3.5 h-3.5" />
              Fix now
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-red-300 text-xs mb-3" data-testid="country-audit-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className={`rounded-xl p-3 mb-4 ${meta.bg} border ${meta.border}`} data-testid="country-audit-recommendation">
            <p className={`text-xs ${meta.text}`}>{data.summary.recommendation}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {["dealers", "customers"].map((coll) => {
              const info = data[coll];
              return (
                <div
                  key={coll}
                  className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-3"
                  data-testid={`country-audit-summary-${coll}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300 text-xs font-semibold uppercase tracking-wide">
                      {coll}
                    </span>
                    <span className="text-gray-500 text-[11px] tabular-nums">
                      {info.total} totali
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] tabular-nums">
                    <span className="text-red-300">
                      🔴 {info.drift_count} drift
                    </span>
                    <span className="text-amber-300">
                      ⚠️ {info.null_count} null
                    </span>
                    <span className="text-emerald-300">
                      ✅ {info.total - info.drift_count - info.null_count} ok
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-gray-400 hover:text-gray-200 text-xs mb-2 transition-colors"
            data-testid="country-audit-expand-btn"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {expanded ? "Nascondi" : "Mostra"} breakdown per country
          </button>

          {expanded && (
            <div className="space-y-4" data-testid="country-audit-details">
              {["dealers", "customers"].map((coll) => {
                const info = data[coll];
                return (
                  <div key={coll} className="border border-gray-800 rounded-xl overflow-hidden">
                    <p className="bg-[#0a0a0a] px-3 py-2 text-gray-300 text-xs font-semibold uppercase tracking-wide border-b border-gray-800">
                      {coll} — breakdown
                    </p>
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-[#0a0a0a] text-gray-500">
                          <th className="px-3 py-1.5 text-left w-8">•</th>
                          <th className="px-3 py-1.5 text-left">Country</th>
                          <th className="px-3 py-1.5 text-right">N. record</th>
                          <th className="px-3 py-1.5 text-left">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {info.breakdown.map((r) => (
                          <tr key={String(r.country)} className="border-t border-gray-800/60">
                            <td className="px-3 py-1.5">{ROW_ICON[r.status] || "·"}</td>
                            <td className="px-3 py-1.5 text-white font-mono">
                              {r.country === null ? "<null>" : r.country}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-300 tabular-nums">{r.count}</td>
                            <td className="px-3 py-1.5 text-gray-500">
                              {r.status === "iso_drift" && (
                                <>ISO drift → atteso <code className="text-red-300">{r.expected_legacy}</code> (legacy)</>
                              )}
                              {r.status === "null" && "Country non assegnato"}
                              {r.status === "ok" && "OK"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {(info.drift_samples.length > 0 || info.null_samples.length > 0) && (
                      <div className="border-t border-gray-800 bg-[#080808] p-3 space-y-2">
                        {info.drift_samples.length > 0 && (
                          <div>
                            <p className="text-red-300 text-[10px] uppercase font-semibold mb-1">
                              🔴 Primi {info.drift_samples.length} record in drift
                            </p>
                            <ul className="space-y-0.5 text-[11px] text-gray-400" data-testid={`country-audit-${coll}-drift-samples`}>
                              {info.drift_samples.map((s) => (
                                <li key={s.id} className="truncate">
                                  <span className="text-red-300 font-mono">{s.country}</span>
                                  <span className="text-gray-600"> · </span>
                                  <span className="text-gray-300">{s.name}</span>
                                  {s.code && <><span className="text-gray-600"> · </span>{s.code}</>}
                                  <span className="text-gray-600"> · </span>
                                  <span className="text-gray-500">{s.email}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {info.null_samples.length > 0 && (
                          <div>
                            <p className="text-amber-300 text-[10px] uppercase font-semibold mb-1">
                              ⚠️ Primi {info.null_samples.length} record senza country
                            </p>
                            <ul className="space-y-0.5 text-[11px] text-gray-400" data-testid={`country-audit-${coll}-null-samples`}>
                              {info.null_samples.map((s) => (
                                <li key={s.id} className="truncate">
                                  <span className="text-gray-300">{s.name}</span>
                                  {s.code && <><span className="text-gray-600"> · </span>{s.code}</>}
                                  <span className="text-gray-600"> · </span>
                                  <span className="text-gray-500">{s.email}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[10px] text-gray-500 mt-4">
            Ultimo controllo: {fmtTime(data.checked_at)} · Endpoint read-only, nessuna modifica applicata
          </p>
        </>
      )}

      {/* Fix-now confirmation modal */}
      {showApplyModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[400] flex items-center justify-center p-4"
          onClick={() => !applying && setShowApplyModal(false)}
          data-testid="country-audit-apply-modal"
        >
          <div
            className="bg-[#111] border border-gray-700 rounded-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-red-300" />
                </div>
                <h4 className="text-white text-lg font-semibold">Allinea country codes al formato legacy</h4>
              </div>
              {!applying && (
                <button
                  type="button"
                  onClick={() => setShowApplyModal(false)}
                  className="text-gray-500 hover:text-gray-200"
                  data-testid="country-audit-apply-modal-close"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {!applyResult && (
              <>
                <p className="text-gray-300 text-sm mb-3">
                  Stai per allineare <strong className="text-red-300">{data.summary.total_drift} record</strong>{" "}
                  in ISO drift al codice legacy LoJack usato dal backend.
                </p>
                <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-3 mb-4 text-xs text-gray-400 space-y-1">
                  {Object.entries(data.iso_drift_map).map(([iso, legacy]) => (
                    <div key={iso} className="flex items-center justify-between">
                      <span>
                        <code className="text-red-300">{iso}</code>
                        <span className="text-gray-600 mx-2">→</span>
                        <code className="text-emerald-300">{legacy}</code>
                      </span>
                      <span className="tabular-nums text-gray-500">
                        {data.dealers.breakdown.find((b) => b.country === iso)?.count || 0} dealer ·{" "}
                        {data.customers.breakdown.find((b) => b.country === iso)?.count || 0} customer
                      </span>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 mb-4 text-xs text-amber-200">
                  <strong>Nota:</strong> operazione idempotente e tracciata in <code>admin_audit</code>.
                  I customer cancellati (deleted_at) non vengono toccati.
                  Puoi rilanciarla in sicurezza — le seconde esecuzioni non fanno nulla.
                </div>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    disabled={applying}
                    className="px-4 py-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-300 rounded-lg text-sm disabled:opacity-50"
                    data-testid="country-audit-apply-cancel"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={applyLegacy}
                    disabled={applying}
                    className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="country-audit-apply-confirm"
                  >
                    {applying ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Applicazione…
                      </>
                    ) : (
                      <>
                        <Wrench className="w-3.5 h-3.5" />
                        Applica su {data.summary.total_drift} record
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {applyResult?.error && (
              <div
                className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-red-300 text-sm"
                data-testid="country-audit-apply-error"
              >
                <p className="font-semibold mb-1">Errore durante l&apos;allineamento</p>
                <p className="text-xs opacity-90">{applyResult.error}</p>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    className="px-3 py-1.5 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-300 rounded-lg text-xs"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            )}

            {applyResult && !applyResult.error && (
              <div data-testid="country-audit-apply-success">
                <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    <p className="text-emerald-300 font-semibold text-sm">Allineamento completato</p>
                  </div>
                  <p className="text-emerald-200/90 text-xs">
                    Aggiornati <strong>{applyResult.updated_dealers}</strong> dealer e{" "}
                    <strong>{applyResult.updated_customers}</strong> customer.
                    L&apos;audit è stato ricaricato automaticamente.
                  </p>
                </div>
                {applyResult.detail && applyResult.detail.length > 0 && (
                  <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-3 mb-3 text-xs">
                    <p className="text-gray-400 uppercase text-[10px] tracking-wide font-semibold mb-1.5">
                      Dettaglio
                    </p>
                    {applyResult.detail.map((row) => (
                      <div key={`${row.from}-${row.to}`} className="text-gray-300 tabular-nums">
                        <code className="text-red-300">{row.from}</code>
                        <span className="text-gray-600 mx-1.5">→</span>
                        <code className="text-emerald-300">{row.to}</code>
                        <span className="text-gray-500 ml-3">
                          {row.dealers} dealer · {row.customers} customer
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    className="px-4 py-2 bg-[#1A1A1A] hover:bg-[#222] border border-gray-700 text-gray-300 rounded-lg text-sm"
                    data-testid="country-audit-apply-done"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryAuditWidget;
